/**
 * Google Analytics GA4 Service
 * Serviço completo para tracking de eventos de negócio e análise de comportamento do usuário
 */

// Declaração global do gtag
declare global {
  interface Window {
    gtag: (command: string, targetId: string | Date, config?: Record<string, any>) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface UserProperties {
  user_type?: 'free' | 'premium' | 'admin' | 'demo';
  subscription_status?: 'active' | 'inactive' | 'trial';
  preferred_language?: 'pt' | 'en';
  registration_date?: string;
  last_exam_date?: string;
  total_exams_taken?: number;
  average_score?: number;
}

export interface ConversionEvent {
  event_name: string;
  currency?: string;
  value?: number;
  transaction_id?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    category: string;
    quantity?: number;
    price?: number;
  }>;
}

class AnalyticsService {
  private isInitialized = false;
  private readonly GA_MEASUREMENT_ID = 'G-JW0T03931E';

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
    }
  }

  private ensureInitialized(): boolean {
    if (!this.isInitialized && typeof window !== 'undefined' && window.gtag) {
      this.initialize();
    }
    return this.isInitialized;
  }

  /**
   * TRACKING DE PÁGINAS
   * Rastreia visualizações de página com contexto de negócio
   */
  trackPageView(pageName: string, additionalParams?: Record<string, any>): void {
    if (!this.ensureInitialized()) return;

    const pageParams = {
      page_title: `CertifyBase - ${pageName}`,
      page_location: window.location.href,
      page_referrer: document.referrer,
      ...additionalParams
    };

    window.gtag('config', this.GA_MEASUREMENT_ID, pageParams);
    
    // Evento personalizado para melhor segmentação
    this.trackEvent({
      action: 'page_view',
      category: 'navigation',
      label: pageName,
      custom_parameters: pageParams
    });
  }

  /**
   * EVENTOS DE AUTENTICAÇÃO
   * Cruciais para entender funil de conversão
   */
  trackAuth = {
    signUp: (method: 'email' | 'google' | 'github' | 'linkedin') => {
      this.trackEvent({
        action: 'sign_up',
        category: 'authentication',
        label: method,
        custom_parameters: { method }
      });
      
      // Evento de conversão
      this.trackConversion({
        event_name: 'sign_up',
        custom_parameters: { method }
      });
    },

    signIn: (method: 'email' | 'google' | 'github' | 'linkedin') => {
      this.trackEvent({
        action: 'login',
        category: 'authentication',
        label: method,
        custom_parameters: { method }
      });
    },

    signOut: () => {
      this.trackEvent({
        action: 'logout',
        category: 'authentication'
      });
    }
  };

  /**
   * EVENTOS DE SIMULADOS
   * Core business - métricas mais importantes
   */
  trackExam = {
    start: (examId: string, examName: string, vendor: string) => {
      this.trackEvent({
        action: 'exam_start',
        category: 'engagement',
        label: examName,
        custom_parameters: {
          exam_id: examId,
          exam_name: examName,
          vendor: vendor,
          timestamp: new Date().toISOString()
        }
      });
    },

    complete: (examId: string, examName: string, score: number, passed: boolean, timeSpent: number) => {
      this.trackEvent({
        action: 'exam_complete',
        category: 'engagement',
        label: examName,
        value: score,
        custom_parameters: {
          exam_id: examId,
          exam_name: examName,
          score: score,
          passed: passed,
          time_spent_minutes: Math.round(timeSpent / 60),
          result_status: passed ? 'passed' : 'failed'
        }
      });

      // Conversão para exames aprovados
      if (passed) {
        this.trackConversion({
          event_name: 'exam_passed',
          value: score,
          custom_parameters: {
            exam_id: examId,
            exam_name: examName,
            score: score
          }
        });
      }
    },

    abandon: (examId: string, examName: string, questionsAnswered: number, totalQuestions: number) => {
      this.trackEvent({
        action: 'exam_abandon',
        category: 'engagement',
        label: examName,
        value: Math.round((questionsAnswered / totalQuestions) * 100),
        custom_parameters: {
          exam_id: examId,
          exam_name: examName,
          questions_answered: questionsAnswered,
          total_questions: totalQuestions,
          completion_rate: Math.round((questionsAnswered / totalQuestions) * 100)
        }
      });
    }
  };

  /**
   * EVENTOS DE NAVEGAÇÃO E ENGAJAMENTO
   * Para entender comportamento do usuário
   */
  trackNavigation = {
    vendorClick: (vendorName: string, vendorId: string) => {
      this.trackEvent({
        action: 'vendor_click',
        category: 'navigation',
        label: vendorName,
        custom_parameters: {
          vendor_id: vendorId,
          vendor_name: vendorName
        }
      });
    },

    catalogFilter: (filterType: string, filterValue: string) => {
      this.trackEvent({
        action: 'catalog_filter',
        category: 'interaction',
        label: `${filterType}: ${filterValue}`,
        custom_parameters: {
          filter_type: filterType,
          filter_value: filterValue
        }
      });
    },

    languageChange: (fromLang: string, toLang: string) => {
      this.trackEvent({
        action: 'language_change',
        category: 'interaction',
        label: `${fromLang} → ${toLang}`,
        custom_parameters: {
          from_language: fromLang,
          to_language: toLang
        }
      });
    },

    themeChange: (theme: 'light' | 'dark' | 'system') => {
      this.trackEvent({
        action: 'theme_change',
        category: 'interaction',
        label: theme,
        custom_parameters: { theme }
      });
    }
  };

  /**
   * EVENTOS DE CONVERSÃO E NEGÓCIO
   * Para análise de ROI e performance
   */
  trackBusiness = {
    ctaClick: (ctaName: string, location: string) => {
      this.trackEvent({
        action: 'cta_click',
        category: 'conversion',
        label: ctaName,
        custom_parameters: {
          cta_name: ctaName,
          cta_location: location
        }
      });
    },

    featureUsage: (featureName: string, context?: string) => {
      this.trackEvent({
        action: 'feature_usage',
        category: 'engagement',
        label: featureName,
        custom_parameters: {
          feature_name: featureName,
          context: context
        }
      });
    },

    errorEncounter: (errorType: string, errorMessage: string, page: string) => {
      this.trackEvent({
        action: 'error_encounter',
        category: 'technical',
        label: errorType,
        custom_parameters: {
          error_type: errorType,
          error_message: errorMessage,
          page: page,
          timestamp: new Date().toISOString()
        }
      });
    }
  };

  /**
   * TRACKING GENÉRICO DE EVENTOS
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.ensureInitialized()) return;

    const eventData = {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event.custom_parameters
    };

    window.gtag('event', event.action, eventData);
  }

  /**
   * TRACKING DE CONVERSÕES
   */
  trackConversion(conversion: ConversionEvent): void {
    if (!this.ensureInitialized()) return;

    window.gtag('event', conversion.event_name, {
      currency: conversion.currency || 'BRL',
      value: conversion.value || 0,
      transaction_id: conversion.transaction_id,
      items: conversion.items,
      ...conversion.custom_parameters
    });
  }

  /**
   * PROPRIEDADES DO USUÁRIO
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.ensureInitialized()) return;

    window.gtag('config', this.GA_MEASUREMENT_ID, {
      custom_map: properties
    });

    // Também envia como evento para segmentação
    this.trackEvent({
      action: 'user_properties_update',
      category: 'user_data',
      custom_parameters: properties
    });
  }

  /**
   * IDENTIFICAÇÃO DO USUÁRIO
   */
  identifyUser(userId: string, properties?: UserProperties): void {
    if (!this.ensureInitialized()) return;

    window.gtag('config', this.GA_MEASUREMENT_ID, {
      user_id: userId,
      ...properties
    });

    if (properties) {
      this.setUserProperties(properties);
    }

    // Track user session start
    this.trackEvent({
      action: 'session_start',
      category: 'engagement',
      custom_parameters: {
        user_id: userId,
        session_duration: 0,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * TRACKING DE SESSÃO
   */
  trackSessionEnd(userId: string, duration: number): void {
    if (!this.ensureInitialized()) return;

    this.trackEvent({
      action: 'session_end',
      category: 'engagement',
      custom_parameters: {
        user_id: userId,
        session_duration: duration,
        timestamp: new Date().toISOString()
      }
    });
  }

  trackLogout(userId: string, reason: 'manual' | 'auto' | 'timeout'): void {
    if (!this.ensureInitialized()) return;

    this.trackEvent({
      action: 'user_logout',
      category: 'authentication',
      custom_parameters: {
        user_id: userId,
        logout_reason: reason,
        timestamp: new Date().toISOString()
      }
    });
  }

  trackActiveUsers(): void {
    if (!this.ensureInitialized()) return;

    // Track active users count (this would typically be called from a backend service)
    this.trackEvent({
      action: 'active_users_check',
      category: 'analytics',
      custom_parameters: {
        timestamp: new Date().toISOString(),
        page_url: window.location.href
      }
    });
  }

  /**
   * TIMING DE PERFORMANCE
   */
  trackTiming(name: string, value: number, category: string = 'performance'): void {
    if (!this.ensureInitialized()) return;

    window.gtag('event', 'timing_complete', {
      name: name,
      value: Math.round(value),
      event_category: category
    });
  }

  /**
   * EXCEÇÕES E ERROS
   */
  trackException(description: string, fatal: boolean = false): void {
    if (!this.ensureInitialized()) return;

    window.gtag('event', 'exception', {
      description: description,
      fatal: fatal
    });
  }
}

// Singleton instance
export const analytics = new AnalyticsService();

// Export para uso em componentes
export default analytics;
